import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { timeTrackingAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Plus, Trash2, Loader2, Clock, Timer } from 'lucide-react';

export default function TimeTracking({ projectId, tasks = [] }) {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({ task_id: '', hours: '', description: '', date: new Date().toISOString().split('T')[0] });

  const fetchEntries = useCallback(async () => {
    try {
      const res = await timeTrackingAPI.listByProject(projectId);
      setEntries(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await timeTrackingAPI.create({ ...form, project_id: projectId, hours: parseFloat(form.hours) || 0 });
      setDialogOpen(false);
      setForm({ task_id: '', hours: '', description: '', date: new Date().toISOString().split('T')[0] });
      fetchEntries();
    } catch (err) { console.error(err); } finally { setFormLoading(false); }
  };

  const handleDelete = async (id) => {
    try { await timeTrackingAPI.delete(id); fetchEntries(); } catch (err) { console.error(err); }
  };

  const totalHours = entries.reduce((s, e) => s + (e.hours || 0), 0);
  const myHours = entries.filter(e => e.user_id === user?.user_id).reduce((s, e) => s + (e.hours || 0), 0);

  // Group by user
  const byUser = {};
  entries.forEach(e => {
    if (!byUser[e.user_name]) byUser[e.user_name] = 0;
    byUser[e.user_name] += e.hours || 0;
  });

  const taskMap = Object.fromEntries(tasks.map(t => [t.task_id, t.title]));

  if (loading) return <div className="animate-pulse h-48 bg-muted rounded" />;

  return (
    <div className="space-y-6" data-testid="time-tracking">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Time Tracking</h3>
        <Button size="sm" onClick={() => setDialogOpen(true)} data-testid="log-time-btn">
          <Plus className="h-4 w-4 mr-1" /> Log Time
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground">Total Hours</p>
          <p className="text-2xl font-bold" data-testid="total-hours">{totalHours.toFixed(1)}h</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground">My Hours</p>
          <p className="text-2xl font-bold" data-testid="my-hours">{myHours.toFixed(1)}h</p>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <p className="text-xs text-muted-foreground">Team Members</p>
          <p className="text-2xl font-bold">{Object.keys(byUser).length}</p>
        </CardContent></Card>
      </div>

      {/* By user breakdown */}
      {Object.keys(byUser).length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-sm">Hours by Team Member</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(byUser).sort((a, b) => b[1] - a[1]).map(([name, hrs]) => (
              <div key={name} className="flex items-center justify-between">
                <span className="text-sm">{name}</span>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 rounded-full bg-muted overflow-hidden">
                    <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min((hrs / totalHours) * 100, 100)}%` }} />
                  </div>
                  <span className="text-sm font-medium w-12 text-right">{hrs.toFixed(1)}h</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Entries list */}
      <Card>
        <CardHeader><CardTitle className="text-sm">Recent Entries</CardTitle></CardHeader>
        <CardContent>
          {entries.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground"><Timer className="h-8 w-8 mx-auto mb-2 opacity-30" /><p className="text-sm">No time logged yet</p></div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {entries.slice(0, 20).map(e => (
                <div key={e.entry_id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30 hover:bg-muted/50 group" data-testid={`time-entry-${e.entry_id}`}>
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{e.hours}h</span>
                        <span className="text-xs text-muted-foreground">by {e.user_name}</span>
                        <span className="text-xs text-muted-foreground">{e.date}</span>
                      </div>
                      {e.description && <p className="text-xs text-muted-foreground">{e.description}</p>}
                      {e.task_id && taskMap[e.task_id] && <p className="text-xs text-primary">{taskMap[e.task_id]}</p>}
                    </div>
                  </div>
                  {(e.user_id === user?.user_id || user?.role === 'admin') && (
                    <Button variant="ghost" size="icon" className="h-6 w-6 opacity-0 group-hover:opacity-100 text-destructive" onClick={() => handleDelete(e.entry_id)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Log Time Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Log Time</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Hours</Label><Input type="number" min="0.1" step="0.1" value={form.hours} onChange={e => setForm({...form, hours: e.target.value})} required data-testid="time-hours-input" /></div>
                <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required /></div>
              </div>
              {tasks.length > 0 && (
                <div className="space-y-2">
                  <Label>Task (optional)</Label>
                  <select className="w-full h-9 px-3 rounded-md border border-border bg-background text-sm" value={form.task_id} onChange={e => setForm({...form, task_id: e.target.value})}>
                    <option value="">General project work</option>
                    {tasks.map(t => <option key={t.task_id} value={t.task_id}>{t.title}</option>)}
                  </select>
                </div>
              )}
              <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="What did you work on?" data-testid="time-desc-input" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-time-btn">{formLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Log Time</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
